import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CsrAPI } from "../../apis/CsrAPI";

const initialState = {
  studyId: null,
  status: "idle", // 'idle' | 'polling' | 'ready' | 'error'
  pollingError: null,
  tables: { data: [], loading: false, error: null, fetched: false },
  figures: { data: [], loading: false, error: null, fetched: false },
};

const dedupeStrings = list => {
  if (!Array.isArray(list)) return [];
  return [...new Set(list.filter(s => typeof s === "string" && s.length > 0))];
};

const isCancellation = err =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

export const fetchTableNames = createAsyncThunk(
  "csrData/fetchTableNames",
  async (studyId, { rejectWithValue }) => {
    try {
      const res = await CsrAPI.getTableNames({ studyId });
      return dedupeStrings(res);
    } catch (err) {
      if (isCancellation(err)) {
        return rejectWithValue("cancelled");
      }
      return rejectWithValue(err?.message || "Failed to load table names");
    }
  },
  {
    condition: (studyId, { getState }) => {
      if (!studyId) return false;
      const slice = getState().csrData;
      if (slice.studyId !== studyId) return true;
      // Already fetched successfully — nothing to do.
      if (slice.tables.fetched && !slice.tables.error) return false;
      // Already in flight — don't stack a second request that would just
      // cancel the first and leave the loading flag pinned.
      if (slice.tables.loading) return false;
      return true;
    },
  }
);

export const fetchFigureNames = createAsyncThunk(
  "csrData/fetchFigureNames",
  async (studyId, { rejectWithValue }) => {
    try {
      const res = await CsrAPI.getFigureNames({ studyId });
      return dedupeStrings(res);
    } catch (err) {
      if (isCancellation(err)) {
        return rejectWithValue("cancelled");
      }
      return rejectWithValue(err?.message || "Failed to load figure names");
    }
  },
  {
    condition: (studyId, { getState }) => {
      if (!studyId) return false;
      const slice = getState().csrData;
      if (slice.studyId !== studyId) return true;
      if (slice.figures.fetched && !slice.figures.error) return false;
      if (slice.figures.loading) return false;
      return true;
    },
  }
);

const csrDataSlice = createSlice({
  name: "csrData",
  initialState,
  reducers: {
    resetForStudy: (state, action) => {
      state.studyId = action.payload;
      state.status = "idle";
      state.pollingError = null;
      state.tables = { data: [], loading: false, error: null, fetched: false };
      state.figures = {
        data: [],
        loading: false,
        error: null,
        fetched: false,
      };
    },
    setStatusPolling: state => {
      state.status = "polling";
      state.pollingError = null;
    },
    setStatusReady: state => {
      state.status = "ready";
      state.pollingError = null;
    },
    setStatusError: (state, action) => {
      state.status = "error";
      state.pollingError = action.payload || "Status polling failed";
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTableNames.pending, (state, action) => {
        if (state.studyId !== action.meta.arg) return;
        state.tables.loading = true;
        state.tables.error = null;
      })
      .addCase(fetchTableNames.fulfilled, (state, action) => {
        if (state.studyId !== action.meta.arg) return;
        state.tables.data = action.payload;
        state.tables.loading = false;
        state.tables.fetched = true;
        state.tables.error = null;
      })
      .addCase(fetchTableNames.rejected, (state, action) => {
        if (state.studyId !== action.meta.arg) return;
        // Always clear loading; a cancelled fetch that leaves loading=true
        // would also stop the condition guard from blocking duplicates.
        state.tables.loading = false;
        if (action.payload === "cancelled") return;
        state.tables.error = action.payload || "Failed to load table names";
      })
      .addCase(fetchFigureNames.pending, (state, action) => {
        if (state.studyId !== action.meta.arg) return;
        state.figures.loading = true;
        state.figures.error = null;
      })
      .addCase(fetchFigureNames.fulfilled, (state, action) => {
        if (state.studyId !== action.meta.arg) return;
        state.figures.data = action.payload;
        state.figures.loading = false;
        state.figures.fetched = true;
        state.figures.error = null;
      })
      .addCase(fetchFigureNames.rejected, (state, action) => {
        if (state.studyId !== action.meta.arg) return;
        state.figures.loading = false;
        if (action.payload === "cancelled") return;
        state.figures.error = action.payload || "Failed to load figure names";
      });
  },
});

export const {
  resetForStudy,
  setStatusPolling,
  setStatusReady,
  setStatusError,
} = csrDataSlice.actions;

export default csrDataSlice.reducer;
